// ============================================
// My Routes Page — Driver Panel
// Enhanced Live Navigation with TRUE Fullscreen,
// Live Movement Tracking & Route Highlighting
// ============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';
import { driverMenuItems } from './driverNav';
import socketService from '../../services/socket';
import { GoogleMap, Marker, DirectionsService, Polyline } from '@react-google-maps/api';
import AnimatedMarker from '../../components/common/AnimatedMarker';
import { getCarMarkerIcon } from '../../components/common/CarMarkerIcon';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import LocationPermissionBanner from '../../components/common/LocationPermissionBanner';
import useDriverTracking from '../../hooks/useDriverTracking';
import RouteMapPicker from '../../components/common/RouteMapPicker';
import { useToast } from '../../context/ToastContext';
import { groupRoutes } from '../../utils/routeUtils';
import { getPremiumMapOptions } from '../../utils/mapStyles';
import { useTheme } from '../../context/ThemeContext';


const DEFAULT_LOC = { lat: 51.5074, lng: -0.1278 };

const decodeHtml = (html) => {
    if (!html) return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    // Handle double/triple encoded entities
    let decoded = txt.value;
    while (decoded.includes('&amp;')) {
        txt.innerHTML = decoded;
        decoded = txt.value;
    }
    return decoded;
};

// ── Utility: find the index of the nearest point on a polyline to a given position ──
const findNearestPointIndex = (points, pos) => {
    if (!points || points.length === 0 || !pos) return 0;
    let minDist = Infinity;
    let minIdx = 0;
    for (let i = 0; i < points.length; i++) {
        const dLat = points[i].lat - pos.lat;
        const dLng = points[i].lng - pos.lng;
        const dist = dLat * dLat + dLng * dLng; // squared distance is fine for comparison
        if (dist < minDist) {
            minDist = dist;
            minIdx = i;
        }
    }
    return minIdx;
};

// ── Fullscreen Live Map Component ──
const LiveNavigationMap = React.memo(({ isLoaded, driverLoc, destination, destinationAddress, startCoords, originAddress, stops, polyColor }) => {
    const [dirResponse, setDirResponse] = useState(null);
    const [dirRequested, setDirRequested] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const mapRef = useRef(null);
    const containerRef = useRef(null);
    const followDriverRef = useRef(true);
    const { showToast } = useToast();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const liveCenter = React.useMemo(() => driverLoc || startCoords || DEFAULT_LOC, [driverLoc, startCoords]);

    // Build directions request with all stops as waypoints
    const dirServiceOptions = React.useMemo(() => {
        if (dirRequested || dirResponse) return null;

        // Use coordinates if available, otherwise fall back to text addresses
        const dest = destination || destinationAddress;
        if (!dest) return null;

        // If recalculating, force origin to be the driver's current live location
        const origin = isRecalculating ? (driverLoc || startCoords || originAddress || DEFAULT_LOC) : (startCoords || originAddress || driverLoc || DEFAULT_LOC);
        const waypoints = (stops || [])
            .filter(s => s.location?.coordinates)
            .map(s => ({
                location: { lat: s.location.coordinates[1], lng: s.location.coordinates[0] },
                stopover: true
            }));

        return {
            origin,
            destination: dest,
            waypoints: waypoints.length > 0 ? waypoints : undefined,
            travelMode: 'DRIVING',
            optimizeWaypoints: false
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destination, destinationAddress, driverLoc, startCoords, originAddress, stops, dirRequested, dirResponse]);

    const handleDirectionsCallback = useCallback((response, status) => {
        setDirRequested(true);
        if (response !== null && status === 'OK') {
            setDirResponse(response);
        } else {
            console.warn('Directions request failed:', status);
            if (status === 'ZERO_RESULTS') {
                showToast('Unable to calculate route from current location. Area might be unmapped.', 'error');
            } else {
                showToast('Route calculation failed: ' + status, 'error');
            }
            // Revert back to original route to avoid infinite loading/blank state
            setIsRecalculating(false);
            setDirRequested(false);
        }
    }, [showToast]);

    // ── Decode route into LatLng array for progressive rendering ──
    const routePoints = React.useMemo(() => {
        if (!dirResponse?.routes?.[0]) return [];
        const path = dirResponse.routes[0].overview_path;
        if (!path || path.length === 0) return [];
        return path.map(p => ({ lat: p.lat(), lng: p.lng() }));
    }, [dirResponse]);

    // ── Split route into traversed (dimmed) and remaining (bright) ──
    const { traversedPath, remainingPath } = React.useMemo(() => {
        if (routePoints.length === 0 || !driverLoc) {
            return { traversedPath: [], remainingPath: routePoints };
        }
        const nearestIdx = findNearestPointIndex(routePoints, driverLoc);
        // Include the nearest point in both paths so there's no visual gap
        return {
            traversedPath: routePoints.slice(0, nearestIdx + 1),
            remainingPath: routePoints.slice(nearestIdx)
        };
    }, [routePoints, driverLoc]);

    // ── Route origin/destination markers from directions response ──
    const routeEndpoints = React.useMemo(() => {
        if (!dirResponse?.routes?.[0]?.legs) return null;
        const legs = dirResponse.routes[0].legs;
        const first = legs[0];
        const last = legs[legs.length - 1];
        return {
            origin: { lat: first.start_location.lat(), lng: first.start_location.lng() },
            destination: { lat: last.end_location.lat(), lng: last.end_location.lng() }
        };
    }, [dirResponse]);

    const baseColor = polyColor || '#0f766e';

    // Live follow driver position — pan smoothly when driver moves
    useEffect(() => {
        if (mapRef.current && driverLoc && followDriverRef.current) {
            mapRef.current.panTo({ lat: driverLoc.lat, lng: driverLoc.lng });
        }
    }, [driverLoc]);

    // Fit bounds when directions first load
    useEffect(() => {
        if (dirResponse && mapRef.current) {
            const bounds = new window.google.maps.LatLngBounds();
            dirResponse.routes[0].legs.forEach(leg => {
                bounds.extend(leg.start_location);
                bounds.extend(leg.end_location);
                leg.steps.forEach(step => {
                    bounds.extend(step.start_location);
                    bounds.extend(step.end_location);
                });
            });
            if (driverLoc) bounds.extend(driverLoc);
            mapRef.current.fitBounds(bounds, { top: 60, bottom: 80, left: 40, right: 40 });
            // After fitting bounds, stop auto-follow until user clicks recenter
            setTimeout(() => { followDriverRef.current = false; }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirResponse]);

    // TRUE Fullscreen using browser Fullscreen API
    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => { });
        } else {
            document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => { });
        }
    }, []);

    // Listen for fullscreen change (e.g. user presses Esc)
    useEffect(() => {
        const handleChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    // Recenter on driver
    const recenterOnDriver = useCallback(() => {
        if (mapRef.current && driverLoc) {
            mapRef.current.panTo(driverLoc);
            mapRef.current.setZoom(16);
            followDriverRef.current = true;
        }
    }, [driverLoc]);

    // When user interacts with map via React events, stop auto-following
    const handleMapInteraction = useCallback(() => {
        followDriverRef.current = false;
    }, []);

    // Raw DOM event listeners for robust touch detection on mobile
    useEffect(() => {
        const mapContainer = containerRef.current;
        if (!mapContainer) return;

        const disableFollow = () => {
            followDriverRef.current = false;
        };

        // Use capture phase to catch interaction before Google Maps consumes it
        mapContainer.addEventListener('touchstart', disableFollow, { passive: true, capture: true });
        mapContainer.addEventListener('mousedown', disableFollow, { passive: true, capture: true });
        mapContainer.addEventListener('wheel', disableFollow, { passive: true, capture: true });

        return () => {
            mapContainer.removeEventListener('touchstart', disableFollow, { capture: true });
            mapContainer.removeEventListener('mousedown', disableFollow, { capture: true });
            mapContainer.removeEventListener('wheel', disableFollow, { capture: true });
        };
    }, []);

    const mapContainerStyle = React.useMemo(() => ({
        width: '100%',
        height: isFullscreen ? '100%' : '500px',
        borderRadius: '0',
        boxShadow: 'inset 0 2px 20px rgba(0,0,0,0.2)'
    }), [isFullscreen]);

    const mapOptions = React.useMemo(() => ({
        ...getPremiumMapOptions(isDark),
        zoomControl: false, // Disable default zoom for cleaner mobile UI (pinch-to-zoom works natively)
    }), [isDark]);

    // Trip info from directions
    const tripInfo = React.useMemo(() => {
        if (!dirResponse?.routes?.[0]?.legs) return null;
        const legs = dirResponse.routes[0].legs;
        let totalDistance = 0;
        let totalDuration = 0;
        legs.forEach(leg => {
            totalDistance += leg.distance.value;
            totalDuration += leg.duration.value;
        });
        return {
            distance: (totalDistance / 1000).toFixed(1) + ' km',
            duration: Math.ceil(totalDuration / 60) + ' mins'
        };
    }, [dirResponse]);

    if (!isLoaded) {
        return (
            <div className="flex items-center justify-center h-[450px]">
                <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div ref={containerRef} className={`relative ${isFullscreen ? 'bg-black w-full h-full' : ''}`}>
            {/* Trip info header */}
            <div className={`flex items-center gap-2 sm:gap-4 px-3 sm:px-4 py-2.5 bg-slate-900 ${isFullscreen ? '' : 'rounded-t-2xl'}`}>
                <span className="w-2 h-2 flex-shrink-0 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] sm:text-[11px] font-black text-white uppercase tracking-widest truncate">LIVE</span>
                <div className="flex items-center gap-1 sm:gap-2 ml-auto flex-shrink-0">
                    {tripInfo ? (
                        <>
                            <span className="px-1.5 sm:px-2.5 py-1 rounded-lg bg-white/10 text-[9px] sm:text-[10px] font-bold text-emerald-300 flex items-center gap-1"><svg className="w-3 h-3 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg> {tripInfo.distance}</span>
                            <span className="px-1.5 sm:px-2.5 py-1 rounded-lg bg-white/10 text-[9px] sm:text-[10px] font-bold text-sky-300 flex items-center gap-1"><svg className="w-3 h-3 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> {tripInfo.duration}</span>
                        </>
                    ) : (
                        <span className="px-2 py-1 rounded-lg bg-white/10 text-[9px] sm:text-[10px] font-bold text-slate-300 animate-pulse flex items-center gap-1">
                            <span className="w-3 h-3 border-2 border-slate-400 border-t-white rounded-full animate-spin" /> Calc
                        </span>
                    )}
                    {/* Fullscreen button in header */}
                    <button
                        onClick={toggleFullscreen}
                        className="ml-1 w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center transition-all active:scale-90"
                        title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                    >
                        {isFullscreen ? (
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="4 14 10 14 10 20" />
                                <polyline points="20 10 14 10 14 4" />
                                <line x1="14" y1="10" x2="21" y2="3" />
                                <line x1="3" y1="21" x2="10" y2="14" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 3 21 3 21 9" />
                                <polyline points="9 21 3 21 3 15" />
                                <line x1="21" y1="3" x2="14" y2="10" />
                                <line x1="3" y1="21" x2="10" y2="14" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Google Map */}
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={liveCenter}
                zoom={14}
                options={mapOptions}
                onLoad={map => { mapRef.current = map; }}
                onDragStart={handleMapInteraction}
                onDrag={handleMapInteraction}
                onZoomChanged={handleMapInteraction}
            >
                {/* Directions API — compute route (one-shot) */}
                {dirServiceOptions && (
                    <DirectionsService
                        options={dirServiceOptions}
                        callback={handleDirectionsCallback}
                    />
                )}

                {/* ── Progressive Route Rendering ── */}
                {/* Traversed portion: dimmed, thinner */}
                {traversedPath.length > 1 && (
                    <Polyline
                        path={traversedPath}
                        options={{
                            strokeColor: baseColor,
                            strokeOpacity: 0.25,
                            strokeWeight: 4,
                            zIndex: 1
                        }}
                    />
                )}
                {/* Remaining portion: bright, thicker */}
                {remainingPath.length > 1 && (
                    <Polyline
                        path={remainingPath}
                        options={{
                            strokeColor: baseColor,
                            strokeOpacity: 0.9,
                            strokeWeight: 6,
                            zIndex: 2
                        }}
                    />
                )}

                {/* Origin marker (green circle) */}
                {routeEndpoints && (
                    <Marker
                        position={routeEndpoints.origin}
                        icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 7,
                            fillColor: '#10b981',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2.5,
                        }}
                        zIndex={100}
                    />
                )}
                {/* Destination marker (red pin) */}
                {routeEndpoints && (
                    <Marker
                        position={routeEndpoints.destination}
                        icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 8,
                            fillColor: '#ef4444',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2.5,
                        }}
                        zIndex={100}
                    />
                )}

                {/* Driver car marker */}
                {driverLoc && (
                    <AnimatedMarker
                        map={mapRef.current}
                        position={driverLoc}
                        heading={driverLoc.heading || 0}
                        speed={driverLoc.speed || 0}
                        icon={getCarMarkerIcon('onMission', driverLoc.heading || 0, 0.7)}
                        zIndex={999}
                    />
                )}
            </GoogleMap>

            {/* Map Action Buttons — bottom right, stacked vertically */}
            <div className={`absolute ${isFullscreen ? 'bottom-6' : 'bottom-12'} right-4 sm:right-6 z-[200] flex flex-col gap-3`}>
                {/* Recalculate Route Button */}
                {driverLoc && (
                    <button
                        onClick={() => {
                            setIsRecalculating(prev => !prev);
                            setDirResponse(null);
                            setDirRequested(false);
                            followDriverRef.current = true;
                            if (mapRef.current) mapRef.current.panTo(driverLoc);
                        }}
                        className={`w-12 h-12 rounded-2xl shadow-xl border flex items-center justify-center active:scale-95 transition-all group relative ${
                            isRecalculating 
                                ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-900/20 border-amber-400' 
                                : 'bg-teal-600 hover:bg-teal-700 shadow-teal-900/20 border-teal-500'
                        }`}
                        title={isRecalculating ? "Show Original Route" : "Recalculate Route from Current Location"}
                    >
                        {isRecalculating ? (
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 text-white group-hover:rotate-180 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        )}
                        <span className="hidden sm:block absolute right-14 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest py-1.5 px-3 rounded-lg opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity">
                            {isRecalculating ? "Show Original Route" : "Recalculate Route"}
                        </span>
                    </button>
                )}
                
                {/* Recenter on Driver */}
                <button
                    onClick={recenterOnDriver}
                    className="w-12 h-12 bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-slate-900/10 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-700 active:scale-95 transition-all group relative"
                    title="Recenter on driver"
                >
                    <svg className="w-5 h-5 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v2m0 16v2M2 12h2m16 0h2" />
                    </svg>
                    <span className="hidden sm:block absolute right-14 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest py-1.5 px-3 rounded-lg opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity shadow-lg">
                        Recenter Map
                    </span>
                </button>
            </div>
        </div>
    );
});
LiveNavigationMap.displayName = 'LiveNavigationMap';


// ── Sub-component for each Route Leg ──
const RouteLegItem = React.memo(({ route: leg, routeGroup, previewRouteId, togglePreview, handleToggleRoute, isActive, isLoaded, driverLoc, disabled = false, disabledMessage = '' }) => {
    const [nowLocal, setNowLocal] = useState(() => new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setNowLocal(new Date());
        }, 1000); // Update every second for live countdown
        return () => clearInterval(timer);
    }, []);

    const isCompleted = leg.status === 'completed';

    const { isReadyToStart, timeUntilReady } = React.useMemo(() => {
        if (!leg.scheduledTime) return { isReadyToStart: true, timeUntilReady: '' };
        const [hours, minutes] = leg.scheduledTime.split(':').map(Number);
        
        // Build the target datetime: use school startDate if it's in the future, otherwise use today
        let targetDate = new Date(nowLocal);
        
        // Check if school has a startDate that hasn't arrived yet
        if (leg.schoolId?.startDate) {
            const schoolStart = new Date(leg.schoolId.startDate);
            const todayUK = new Date(nowLocal);
            todayUK.setHours(0, 0, 0, 0);
            const schoolStartDay = new Date(schoolStart);
            schoolStartDay.setHours(0, 0, 0, 0);
            
            if (schoolStartDay > todayUK) {
                // School hasn't started yet — use the school start date
                targetDate = new Date(schoolStart);
            }
        }
        
        targetDate.setHours(hours, minutes, 0, 0);
        
        const diffMs = targetDate - nowLocal;
        const ready = diffMs <= 0;
        
        // Build a human-readable countdown
        let countdown = '';
        if (!ready) {
            const totalSec = Math.ceil(diffMs / 1000);
            const days = Math.floor(totalSec / 86400);
            const hrs = Math.floor((totalSec % 86400) / 3600);
            const mins = Math.floor((totalSec % 3600) / 60);
            const secs = totalSec % 60;
            
            if (days > 0) {
                countdown = `${days}d ${hrs}h ${mins}m`;
            } else if (hrs > 0) {
                countdown = `${hrs}h ${mins}m ${secs}s`;
            } else if (mins > 0) {
                countdown = `${mins}m ${secs}s`;
            } else {
                countdown = `${secs}s`;
            }
        }
        
        return { isReadyToStart: ready, timeUntilReady: countdown };
    }, [leg.scheduledTime, leg.schoolId?.startDate, nowLocal]);

    const destination = React.useMemo(() => {
        if (leg.endLocation?.coordinates) {
            return { lat: leg.endLocation.coordinates[1], lng: leg.endLocation.coordinates[0] };
        }
        if (leg.schoolId?.location?.coordinates) {
            return { lat: leg.schoolId.location.coordinates[1], lng: leg.schoolId.location.coordinates[0] };
        }
        return null;
    }, [leg]);

    const startCoords = React.useMemo(() => {
        if (leg.startLocation?.coordinates) {
            return { lat: leg.startLocation.coordinates[1], lng: leg.startLocation.coordinates[0] };
        }
        return null;
    }, [leg]);

    const preview = React.useMemo(() => ({
        from: leg.startAddress || leg.stops?.[0]?.address || '',
        to: leg.endAddress || decodeHtml(leg.schoolId?.name) || '',
        fromCoords: leg.startLocation?.coordinates
            ? { lat: leg.startLocation.coordinates[1], lng: leg.startLocation.coordinates[0] }
            : null,
        toCoords: leg.endLocation?.coordinates
            ? { lat: leg.endLocation.coordinates[1], lng: leg.endLocation.coordinates[0] }
            : null
    }), [leg]);

    const isPreviewing = previewRouteId === leg._id;

    const displayBadge = routeGroup?.isMerged ? `${leg.type} MISSION` : leg.type;
    // const badgeColor = leg.type === 'morning' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700';
    const displayTime = leg.scheduledTime;

    return (
        <div className={`relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 p-6 sm:p-8 shadow-xl shadow-slate-200/50 dark:shadow-black/50 transition-all duration-500 group overflow-hidden ${disabled ? 'opacity-50 pointer-events-none grayscale-[30%]' : 'hover:shadow-2xl hover:shadow-teal-500/20 dark:hover:shadow-teal-500/10'}`}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal-50 dark:bg-teal-500/5 rounded-full -mr-16 -mt-16 opacity-20 group-hover:scale-150 transition-transform duration-500" />

            {/* Route Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 relative z-10">
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-xl border text-[10px] font-black uppercase tracking-wider shadow-sm ${leg.type === 'morning' ? 'bg-amber-50 dark:bg-slate-900/80 text-amber-600 dark:text-amber-400 border-amber-500/30' : 'bg-indigo-50 dark:bg-slate-900/80 text-indigo-600 dark:text-indigo-400 border-indigo-500/30'}`}>
                            {displayBadge}
                        </span>
                        {isActive && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-slate-900/80 backdrop-blur-xl border border-emerald-500/30 text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                                ACTIVE MISSION
                            </span>
                        )}
                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{displayTime}</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors uppercase leading-none">{decodeHtml(leg.name)}</h3>
                    {leg.startAddress && (
                        <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> {leg.startAddress.split(',')[0]} → {(leg.endAddress || decodeHtml(leg.schoolId?.name) || '').split(',')[0]}</p>
                    )}
                    {leg.notes && (
                        <p className="text-[11px] font-bold text-amber-600 dark:text-amber-400 mt-2 bg-amber-50 dark:bg-amber-500/10 inline-flex items-start gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-500/20 max-w-full">
                            <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> 
                            <span className="whitespace-normal leading-relaxed">{leg.notes}</span>
                        </p>
                    )}
                    {disabled && disabledMessage && (
                        <p className="text-[11px] font-bold text-rose-500 mt-2 bg-rose-50 dark:bg-rose-500/10 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg> {disabledMessage}
                        </p>
                    )}
                </div>
                {isCompleted ? (
                    <div className="w-full sm:w-auto px-8 py-3.5 rounded-2xl text-xs font-black bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50 flex items-center justify-center gap-2 whitespace-nowrap cursor-default">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        COMPLETED
                    </div>
                ) : (isActive || isReadyToStart) ? (
                    <button
                        onClick={() => handleToggleRoute(leg._id, leg.status)}
                        disabled={disabled}
                        className={`w-full sm:w-auto px-8 py-4 rounded-2xl text-xs font-black transition-all shadow-xl whitespace-nowrap ${isActive
                            ? 'bg-gradient-to-r from-rose-500 to-rose-600 border border-rose-400/30 text-white hover:from-rose-600 hover:to-rose-700 shadow-rose-600/30 active:scale-95 transform'
                            : 'bg-gradient-to-r from-teal-500 to-teal-600 border border-teal-400/30 text-white hover:from-teal-600 hover:to-teal-700 shadow-teal-600/30 active:scale-95 transform'
                            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isActive ? '■ END MISSION' : 'START MISSION →'}
                    </button>
                ) : (
                    <div className="w-full sm:w-auto px-8 py-3.5 rounded-2xl text-xs font-black bg-slate-100 dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700 flex items-center justify-center gap-2 whitespace-nowrap cursor-not-allowed">
                        <svg className="w-4 h-4 animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span className="font-mono">{timeUntilReady}</span>
                        <span className="text-[9px] font-bold text-slate-300 uppercase">until {leg.scheduledTime}</span>
                    </div>
                )}
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-white/60 dark:border-white/5 relative z-10">
                <div className="flex items-center gap-4 p-4 rounded-3xl bg-white/40 dark:bg-slate-800/20 backdrop-blur-md border border-white/80 dark:border-white/5 hover:bg-white/60 dark:hover:bg-slate-700/30 hover:shadow-lg hover:shadow-teal-500/5 transition-all group/info">
                    <div className="w-12 h-12 rounded-2xl bg-white dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 shadow-sm border border-slate-100 dark:border-white/5 group-hover/info:scale-110 transition-transform">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-1">Destination School</p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200 truncate">{decodeHtml(leg.schoolId?.name)}</p>
                    </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-3xl bg-white/40 dark:bg-slate-800/20 backdrop-blur-md border border-white/80 dark:border-white/5 hover:bg-white/60 dark:hover:bg-slate-700/30 hover:shadow-lg hover:shadow-teal-500/5 transition-all group/info">
                    <div className="w-12 h-12 rounded-2xl bg-white dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 shadow-sm border border-slate-100 dark:border-white/5 group-hover/info:scale-110 transition-transform">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-1">Students</p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">{leg.children?.length || 0} Students</p>
                    </div>
                </div>
            </div>

            {/* Assigned Students Section */}
            {leg.children && leg.children.length > 0 && (
                <div className="mt-5 bg-teal-50/50 dark:bg-teal-900/10 rounded-2xl p-4 border border-teal-100/50 dark:border-teal-900/20 relative z-10 transition-all">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="w-2 h-2 rounded-full bg-teal-500 shadow-sm"></span>
                        <p className="text-[10px] font-black uppercase tracking-widest text-teal-700 dark:text-teal-400">Assigned Students ({leg.children.length})</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[200px] overflow-y-auto custom-scrollbar pr-2">
                        {leg.children.map(child => (
                            <div key={child._id} className="bg-white/80 dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400 flex items-center justify-center shrink-0 font-bold text-sm shadow-inner">
                                    {child.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{child.name}</p>
                                    <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate flex items-center gap-1.5 mt-0.5" title={child.pickupLocation?.address || 'No pickup location'}>
                                        <svg className="w-3 h-3 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        <span className="truncate">{child.pickupLocation?.address || 'No pickup location'}</span>
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Pre-Mission Route Preview */}
            {!isActive && !disabled && (
                <div className="mt-5 relative z-10">
                    <button
                        onClick={() => togglePreview(leg._id)}
                        className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all transform active:scale-95 ${isPreviewing
                                ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                                : 'bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700'
                            }`}
                    >
                        <span className="text-base">
                            <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                        </span>
                        {isPreviewing ? 'Hide Route Map' : 'View Route Map'}
                        <svg className={`w-4 h-4 transition-transform duration-300 ${isPreviewing ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {isPreviewing && (
                        <div className="mt-6 mx-[-1.5rem] sm:mx-[-2rem] mb-[-1.5rem] sm:mb-[-2rem] relative z-10 overflow-hidden rounded-b-[2.5rem]">
                            <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 dark:bg-slate-800/80 border-y border-slate-200 dark:border-slate-700">
                                <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse shadow-[0_0_8px_rgba(20,184,166,0.8)]" />
                                <span className="text-[10px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">Route Overview</span>
                            </div>
                            <RouteMapPicker
                                fromAddress={preview.from}
                                toAddress={preview.to}
                                fromCoords={preview.fromCoords}
                                toCoords={preview.toCoords}
                                height="400px"
                                theme="teal"
                                readOnly={true}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Live Navigation Map */}
            {isActive && (
                <div className="mt-8 mx-[-1.5rem] sm:mx-[-2rem] mb-[-1.5rem] sm:mb-[-2rem] relative z-10">
                    <div className="w-full h-full overflow-hidden shadow-inner border-t border-white/60 dark:border-white/5 bg-slate-900 rounded-b-[2.5rem]">
                        <LiveNavigationMap
                            isLoaded={isLoaded}
                            driverLoc={driverLoc}
                            destination={destination}
                            startCoords={startCoords}
                            stops={leg.stops}
                            polyColor="#0f766e"
                        />
                    </div>
                </div>
            )}
        </div>
    );
});
RouteLegItem.displayName = 'RouteLegItem';

// ── Major Container for grouped/single routes ──
const RouteItem = React.memo(({ route, previewRouteId, togglePreview, handleToggleRoute, activeRouteId, isLoaded, driverLoc }) => {
    if (route.isMerged) {
        const isMorningCompleted = route.morningRoute.status === 'completed';
        const isAfternoonCompleted = route.afternoonRoute.status === 'completed';
        
        return (
            <div className="relative bg-gradient-to-br from-violet-50/50 to-indigo-50/50 dark:from-slate-900/40 dark:to-slate-800/40 backdrop-blur-3xl rounded-[3rem] border-2 border-violet-100 dark:border-violet-900/30 p-4 sm:p-6 shadow-2xl shadow-violet-500/10 overflow-hidden mb-6 group/container">
                <div className="absolute -top-32 -right-32 w-64 h-64 bg-violet-200 dark:bg-violet-900/20 rounded-full blur-3xl opacity-50 group-hover/container:opacity-70 transition-opacity duration-700" />
                
                {/* Unified Header */}
                <div className="mb-6 px-4 relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 rounded-lg text-[10px] font-black bg-gradient-to-r from-amber-100 to-indigo-100 text-violet-700 uppercase tracking-wider shadow-sm border border-violet-200/50">
                            MORNING & AFTERNOON SHIFT
                        </span>
                        {isMorningCompleted && isAfternoonCompleted && (
                            <span className="px-3 py-1 rounded-lg text-[10px] font-black bg-emerald-100 text-emerald-700 uppercase tracking-wider shadow-sm border border-emerald-200/50">
                                ALL MISSIONS COMPLETED
                            </span>
                        )}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight drop-shadow-sm">
                        {decodeHtml(route.morningRoute.schoolId?.name)}
                    </h2>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">
                        Unified Driver Assignment • Both missions must be completed by the same driver.
                    </p>
                </div>

                <div className="space-y-6 relative z-10">
                    <RouteLegItem 
                        route={route.morningRoute} 
                        routeGroup={route}
                        previewRouteId={previewRouteId}
                        togglePreview={togglePreview}
                        handleToggleRoute={handleToggleRoute}
                        isActive={activeRouteId === route.morningRoute._id}
                        isLoaded={isLoaded}
                        driverLoc={driverLoc}
                        disabled={false}
                    />
                    
                    <div className="relative">
                        {/* Connecting visual line between Morning & Afternoon */}
                        <div className="absolute -top-6 left-12 w-1 h-6 bg-gradient-to-b from-teal-500 to-indigo-500 rounded-full opacity-30" />
                        
                        <RouteLegItem 
                            route={route.afternoonRoute} 
                            routeGroup={route}
                            previewRouteId={previewRouteId}
                            togglePreview={togglePreview}
                            handleToggleRoute={handleToggleRoute}
                            isActive={activeRouteId === route.afternoonRoute._id}
                            isLoaded={isLoaded}
                            driverLoc={driverLoc}
                            disabled={!isMorningCompleted}
                            disabledMessage="Starts after Morning Mission is completed"
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Normal non-merged route
    return (
        <RouteLegItem 
            route={route} 
            routeGroup={null}
            previewRouteId={previewRouteId}
            togglePreview={togglePreview}
            handleToggleRoute={handleToggleRoute}
            isActive={activeRouteId === route._id}
            isLoaded={isLoaded}
            driverLoc={driverLoc}
            disabled={false}
        />
    );
});

// ── Sub-component for each Job Item ──
const JobItem = React.memo(({ job, previewJobId, togglePreviewJob, handleToggleJob, isActive, isLoaded, driverLoc }) => {
    const [nowLocal, setNowLocal] = useState(() => new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setNowLocal(new Date());
        }, 1000); // Update every second for live countdown
        return () => clearInterval(timer);
    }, []);

    const { isReadyToStart, timeUntilReady } = React.useMemo(() => {
        if (!job.scheduledDate || !job.scheduledTime) return { isReadyToStart: true, timeUntilReady: '' };
        const taskTime = new Date(job.scheduledDate);
        const [hours, minutes] = job.scheduledTime.split(':').map(Number);
        taskTime.setHours(hours, minutes, 0, 0);
        
        const diffMs = taskTime - nowLocal;
        const ready = diffMs <= 0;
        
        let countdown = '';
        if (!ready) {
            const totalSec = Math.ceil(diffMs / 1000);
            const days = Math.floor(totalSec / 86400);
            const hrs = Math.floor((totalSec % 86400) / 3600);
            const mins = Math.floor((totalSec % 3600) / 60);
            const secs = totalSec % 60;
            
            if (days > 0) {
                countdown = `${days}d ${hrs}h ${mins}m`;
            } else if (hrs > 0) {
                countdown = `${hrs}h ${mins}m ${secs}s`;
            } else if (mins > 0) {
                countdown = `${mins}m ${secs}s`;
            } else {
                countdown = `${secs}s`;
            }
        }
        
        return { isReadyToStart: ready, timeUntilReady: countdown };
    }, [job.scheduledDate, job.scheduledTime, nowLocal]);

    const destination = React.useMemo(() => {
        if (job.dropoffLocation?.coordinates) {
            return { lat: job.dropoffLocation.coordinates[1], lng: job.dropoffLocation.coordinates[0] };
        }
        return null;
    }, [job]);

    const startCoords = React.useMemo(() => {
        if (job.pickupLocation?.coordinates) {
            return { lat: job.pickupLocation.coordinates[1], lng: job.pickupLocation.coordinates[0] };
        }
        return null;
    }, [job]);

    const preview = React.useMemo(() => ({
        from: job.pickupAddress,
        to: job.dropoffAddress,
        fromCoords: job.pickupLocation?.coordinates ? { lat: job.pickupLocation.coordinates[1], lng: job.pickupLocation.coordinates[0] } : null,
        toCoords: job.dropoffLocation?.coordinates ? { lat: job.dropoffLocation.coordinates[1], lng: job.dropoffLocation.coordinates[0] } : null
    }), [job]);

    const isPreviewing = previewJobId === job._id;

    return (
        <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 p-6 sm:p-8 shadow-xl shadow-slate-200/50 dark:shadow-black/50 hover:shadow-2xl hover:shadow-indigo-500/20 dark:hover:shadow-indigo-500/10 transition-all duration-500 group overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 dark:bg-indigo-500/5 rounded-full -mr-16 -mt-16 opacity-20 group-hover:scale-150 transition-transform duration-500" />

            {/* Job Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 relative z-10">
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 dark:bg-slate-900/80 backdrop-blur-xl border border-indigo-500/30 text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider shadow-sm">
                            PRIVATE JOB
                        </span>
                        {isActive && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-slate-900/80 backdrop-blur-xl border border-emerald-500/30 text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                                ACTIVE MISSION
                            </span>
                        )}
                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                            {new Date(job.scheduledDate).toLocaleDateString()} {job.scheduledTime}
                        </span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors uppercase leading-none">{job.name}</h3>
                    {job.pickupAddress && (
                        <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> {job.pickupAddress.split(',')[0]} → {(job.dropoffAddress || '').split(',')[0]}</p>
                    )}
                    {job.notes && (
                        <p className="text-[11px] font-bold text-amber-600 dark:text-amber-400 mt-2 bg-amber-50 dark:bg-amber-500/10 inline-flex items-start gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-500/20 max-w-full">
                            <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            <span className="whitespace-normal leading-relaxed">{job.notes}</span>
                        </p>
                    )}
                </div>
                {(isActive || isReadyToStart) ? (
                    <button
                        onClick={() => handleToggleJob(job._id, job.status)}
                        className={`w-full sm:w-auto px-8 py-3.5 rounded-2xl text-xs font-black transition-all shadow-xl transform active:scale-95 whitespace-nowrap ${isActive
                            ? 'bg-rose-600 text-white hover:bg-rose-700 shadow-rose-600/30'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-600/30'
                            }`}
                    >
                        {isActive ? '■ END MISSION' : 'START MISSION →'}
                    </button>
                ) : (
                    <div className="w-full sm:w-auto px-8 py-3.5 rounded-2xl text-xs font-black bg-slate-100 dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700 flex items-center justify-center gap-2 whitespace-nowrap cursor-not-allowed">
                        <svg className="w-4 h-4 animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span className="font-mono">{timeUntilReady}</span>
                        <span className="text-[9px] font-bold text-slate-300 uppercase">until {job.scheduledTime}</span>
                    </div>
                )}
            </div>

            {/* Info Cards */}
            <div className="pt-6 border-t border-white/60 dark:border-white/5 relative z-10">
                <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/50 dark:bg-slate-800/30 border border-white/80 dark:border-white/5 hover:bg-white/70 dark:hover:bg-slate-700/30 hover:shadow-md transition-all">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 shadow-sm border border-slate-100 dark:border-white/5">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-1">Passenger Info</p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">{job.passengers || 1} Passenger(s)</p>
                    </div>
                </div>
            </div>

            {/* Pre-Mission Route Preview */}
            {!isActive && (
                <div className="mt-5 relative z-10">
                    <button
                        onClick={() => togglePreviewJob(job._id)}
                        className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all transform active:scale-95 ${isPreviewing
                                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                                : 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700'
                            }`}
                    >
                        <span className="text-base">
                            <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                        </span>
                        {isPreviewing ? 'Hide Route Map' : 'View Route Map'}
                        <svg className={`w-4 h-4 transition-transform duration-300 ${isPreviewing ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {isPreviewing && (
                        <div className="mt-4">
                            <div className="rounded-2xl overflow-hidden border border-indigo-100">
                                <div className="bg-indigo-50 px-4 py-2.5 flex items-center gap-2 border-b border-indigo-100">
                                    <span className="w-2 h-2 rounded-full bg-indigo-500" />
                                    <span className="text-[11px] font-black text-indigo-700 uppercase tracking-widest">Pre-Mission Route Overview</span>
                                </div>
                                <RouteMapPicker
                                    fromAddress={preview.from}
                                    toAddress={preview.to}
                                    fromCoords={preview.fromCoords}
                                    toCoords={preview.toCoords}
                                    height="300px"
                                    theme="indigo"
                                    readOnly={true}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Live Navigation Map — with route highlighting */}
            {isActive && (
                <div className="mt-6 mx-[-1.5rem] sm:mx-[-2rem] mb-[-1.5rem] sm:mb-[-2rem] relative z-10">
                    <div className="w-full h-full overflow-hidden shadow-inner border-t border-white/60 dark:border-white/5 bg-slate-900 rounded-b-[2.5rem]">
                        <LiveNavigationMap
                            isLoaded={isLoaded}
                            driverLoc={driverLoc}
                            destination={destination}
                            destinationAddress={job.dropoffAddress}
                            startCoords={startCoords}
                            originAddress={job.pickupAddress}
                            stops={[]}
                            polyColor="#4f46e5"
                        />
                    </div>
                </div>
            )}
        </div>
    );
});
JobItem.displayName = 'JobItem';

const MyRoutes = () => {
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const [routes, setRoutes] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeRouteId, setActiveRouteId] = useState(null);
    // const [activeJobId, setActiveJobId] = useState(null);
    const [previewRouteId, setPreviewRouteId] = useState(null);
    const [previewJobId, setPreviewJobId] = useState(null);
    const [completeModal, setCompleteModal] = useState({ isOpen: false, type: null, entityId: null });
    const [completionForm, setCompletionForm] = useState({ status: 'successful', note: '' });
    const [unverified, setUnverified] = useState(false);

    const { showToast } = useToast();

    const { isLoaded } = useGoogleMaps();

    // ── Unified GPS + Compass Tracking ──
    // Always active so the driver map always shows real position
    const { location: trackedLocation, heading: trackedHeading } = useDriverTracking(user?._id, true, 2000);
    const driverLoc = trackedLocation ? { lat: trackedLocation.lat, lng: trackedLocation.lng, heading: trackedHeading } : null;

    const fetchRoutes = useCallback(async () => {
        try {
            const [routesRes, jobsRes] = await Promise.all([
                api.get('/driver/routes').catch((err) => {
                    if (err.response?.status === 403 && err.response?.data?.unverified) {
                        setUnverified(true);
                    }
                    return { data: { success: false } };
                }),
                api.get('/driver/accepted-jobs').catch(() => ({ data: { success: false } }))
            ]);

            if (routesRes.data.success) {
                setUnverified(false);
                const grouped = groupRoutes(routesRes.data.data);
                setRoutes(grouped);
                const startedRoute = routesRes.data.data.find(r => r.status === 'started');
                if (startedRoute) {
                    setActiveRouteId(startedRoute._id);
                }
            }
            if (!unverified && jobsRes.data.success) {
                setJobs(jobsRes.data.data);
                // const startedJob = jobsRes.data.data.find(j => j.status === 'started');
                // if (startedJob) setActiveJobId(startedJob._id);
            }
        } catch (error) {
            console.error('Fetch routes/jobs error:', error);
        } finally {
            setLoading(false);
        }
    }, [unverified]);

    useEffect(() => {
        fetchRoutes();
        const interval = setInterval(fetchRoutes, 10000);

        const handleRouteAssigned = (route) => {
            showToast('info', `New route assigned: ${decodeHtml(route.name)}`);
            fetchRoutes();
        };
        const handleJobAssigned = (job) => {
            showToast('info', `New private job assigned: ${job.name}`);
            fetchRoutes();
        };

        socketService.on('route:assigned', handleRouteAssigned);
        socketService.on('job:assigned', handleJobAssigned);
        socketService.on('route:updated', fetchRoutes);
        socketService.on('job:updated', fetchRoutes);

        // Listen for children changes on routes
        const handleChildrenUpdate = (data) => {
            if (data?.route?.name) {
                const added = data.addedChildren?.length || 0;
                const removed = data.removedChildren?.length || 0;
                if (added > 0) {
                    showToast('info', `${added} new student(s) assigned to ${data.route.name}`);
                } else if (removed > 0) {
                    showToast('info', `Student(s) removed from ${data.route.name}`);
                }
            }
            fetchRoutes();
        };
        socketService.on('route:children:updated', handleChildrenUpdate);

        return () => {
            clearInterval(interval);
            socketService.off('route:assigned', handleRouteAssigned);
            socketService.off('job:assigned', handleJobAssigned);
            socketService.off('route:updated', fetchRoutes);
            socketService.off('job:updated', fetchRoutes);
            socketService.off('route:children:updated', handleChildrenUpdate);
        };
    }, [fetchRoutes, showToast]);

    // Note: GPS tracking is now handled by useDriverTracking hook above.
    // No manual geolocation code needed here.

    const handleToggleRoute = async (routeId, currentStatus) => {
        try {
            if (currentStatus === 'started') {
                setCompleteModal({ isOpen: true, type: 'route', entityId: routeId });
                setCompletionForm({ status: 'successful', note: '' });
                return;
            }
            await api.patch(`/driver/routes/${routeId}/status`, { status: 'started' });
            setActiveRouteId(routeId);
            setPreviewRouteId(null);
            fetchRoutes();
        } catch (error) {
            console.error('Start route error:', error);
        }
    };

    const togglePreview = useCallback((routeId) => {
        setPreviewRouteId((prev) => (prev === routeId ? null : routeId));
    }, []);

    const handleToggleJob = async (jobId, currentStatus) => {
        try {
            if (currentStatus === 'started') {
                setCompleteModal({ isOpen: true, type: 'job', entityId: jobId });
                setCompletionForm({ status: 'successful', note: '' });
                return;
            }
            await api.patch(`/driver/jobs/${jobId}/status`, { status: 'started' });
            // setActiveJobId(jobId);
            setPreviewJobId(null);
            fetchRoutes();
        } catch (error) {
            console.error('Start job error:', error);
        }
    };

    const handleConfirmComplete = async () => {
        const { type, entityId } = completeModal;
        const { status, note } = completionForm;

        try {
            const endpoint = type === 'route' ? `/driver/routes/${entityId}/status` : `/driver/jobs/${entityId}/status`;
            await api.patch(endpoint, {
                status: 'completed',
                completionStatus: status,
                completionNote: note
            });

            if (type === 'route') setActiveRouteId(null);
            // else setActiveJobId(null);
            setCompleteModal({ isOpen: false, type: null, entityId: null });
            fetchRoutes();
        } catch (error) {
            console.error('Complete mission error:', error);
        }
    };

    const togglePreviewJob = useCallback((jobId) => {
        setPreviewJobId((prev) => (prev === jobId ? null : jobId));
    }, []);

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-100 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative">
            <Sidebar items={driverMenuItems} theme="teal" title="IdaCab Driver" />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile Header */}
                <DriverMobileHeader user={user} theme={theme} toggleTheme={toggleTheme} />

                {/* Desktop Header */}
                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <h2 className="text-2xl font-black bg-gradient-to-r from-teal-600 to-teal-400 bg-clip-text text-transparent tracking-tighter">My Assigned Routes</h2>
                </header>

                <main className="flex-1 overflow-y-auto px-4 pt-2 pb-28 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    <LocationPermissionBanner role="driver" />
                    <div className="max-w-5xl mx-auto space-y-6 mt-4">
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : unverified ? (
                            <div className="text-center py-16 bg-gradient-to-br from-rose-50 to-amber-50 dark:from-rose-900/20 dark:to-amber-900/20 backdrop-blur-xl rounded-[2.5rem] border-2 border-dashed border-rose-300/50 dark:border-rose-500/30 shadow-inner">
                                <div className="w-20 h-20 rounded-full bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center mx-auto mb-6 shadow-inner border border-rose-200 dark:border-rose-500/30">
                                    <svg className="w-9 h-9 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h3 className="font-black text-rose-700 dark:text-rose-400 text-lg uppercase tracking-tight">Account Not Verified</h3>
                                <p className="text-xs text-rose-600/80 dark:text-rose-400/70 font-bold uppercase tracking-widest mt-2 max-w-md mx-auto leading-relaxed">
                                    Your compliance documents are incomplete or rejected. Please go to <strong>Documents</strong> to upload/re-upload required documents.
                                </p>
                                <a href="/driver/documents" className="inline-flex items-center gap-2 mt-6 px-8 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest bg-gradient-to-r from-rose-500 to-amber-500 text-white shadow-xl shadow-rose-500/20 hover:from-rose-600 hover:to-amber-600 active:scale-95 transition-all">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    Go to Documents
                                </a>
                            </div>
                        ) : routes.length > 0 || jobs.length > 0 ? (
                            <div className="space-y-10">
                                {routes.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-4">Assigned School Routes</h3>
                                        <div className="grid gap-4 sm:gap-6">
                                            {routes.map((route) => (
                                                <RouteItem
                                                    key={route._id}
                                                    route={route}
                                                    previewRouteId={previewRouteId}
                                                    togglePreview={togglePreview}
                                                    handleToggleRoute={handleToggleRoute}
                                                    activeRouteId={activeRouteId}
                                                    isLoaded={isLoaded}
                                                    driverLoc={driverLoc}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {jobs.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-4">Assigned Private Jobs</h3>
                                        <div className="grid gap-4 sm:gap-6">
                                            {jobs.map((job) => (
                                                <JobItem
                                                    key={job._id}
                                                    job={job}
                                                    previewJobId={previewJobId}
                                                    togglePreviewJob={togglePreviewJob}
                                                    handleToggleJob={handleToggleJob}
                                                    isActive={job.status === 'started'}
                                                    isLoaded={isLoaded}
                                                    driverLoc={driverLoc}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-white/50 dark:bg-slate-900/40 backdrop-blur-xl rounded-[2.5rem] border border-dashed border-slate-300/50 dark:border-slate-700/50 shadow-inner">
                                <div className="w-20 h-20 rounded-full bg-white/50 dark:bg-slate-700/30 flex items-center justify-center mx-auto mb-6 opacity-30 shadow-inner border border-white/60 dark:border-white/5">
                                    <svg className="w-9 h-9 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10l-2.7-3.6A2 2 0 0013.7 5H6.3a2 2 0 00-1.6.9L2 9.5C1.4 9.8 1 10.4 1 11v5c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" /></svg>
                                </div>
                                <h3 className="font-black text-slate-900 dark:text-white text-lg uppercase tracking-tight">No Routes or Jobs Assigned</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-2">Check 'Available Jobs' or contact dispatcher</p>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            {/* ── End Mission Modal ── */}
            {completeModal.isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setCompleteModal({ isOpen: false, type: null, entityId: null })}
                    />

                    <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-slide-up border border-slate-100 dark:border-slate-800">
                        <div className="p-8">
                            <div className="w-16 h-16 bg-teal-50 dark:bg-teal-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner border border-teal-100 dark:border-teal-500/20">
                                <svg className="w-8 h-8 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-center text-slate-900 dark:text-white mb-2 uppercase tracking-tight">End Mission</h3>
                            <p className="text-xs text-center text-slate-500 dark:text-slate-400 font-bold tracking-widest uppercase mb-6">
                                How did the trip go?
                            </p>

                            <div className="space-y-4 mb-6">
                                <div
                                    onClick={() => setCompletionForm(prev => ({ ...prev, status: 'successful' }))}
                                    className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${completionForm.status === 'successful' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10' : 'border-slate-100 dark:border-slate-800 hover:border-emerald-200 dark:hover:border-emerald-500/30'}`}
                                >
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border-2 transition-all ${completionForm.status === 'successful' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 dark:border-slate-600'}`}>
                                        {completionForm.status === 'successful' && <div className="w-2 h-2 rounded-full bg-white" />}
                                    </div>
                                    <div className="font-bold text-slate-900 dark:text-slate-200 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        Completed Successfully
                                    </div>
                                </div>

                                <div
                                    onClick={() => setCompletionForm(prev => ({ ...prev, status: 'issue' }))}
                                    className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${completionForm.status === 'issue' ? 'border-rose-500 bg-rose-50 dark:bg-rose-500/10' : 'border-slate-100 dark:border-slate-800 hover:border-rose-200 dark:hover:border-rose-500/30'}`}
                                >
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border-2 transition-all ${completionForm.status === 'issue' ? 'border-rose-500 bg-rose-500' : 'border-slate-300 dark:border-slate-600'}`}>
                                        {completionForm.status === 'issue' && <div className="w-2 h-2 rounded-full bg-white" />}
                                    </div>
                                    <div className="font-bold text-slate-900 dark:text-slate-200 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                        Ended with Issues
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 pl-1">
                                        Notes (Optional)
                                    </label>
                                    <textarea
                                        value={completionForm.note}
                                        onChange={(e) => setCompletionForm(prev => ({ ...prev, note: e.target.value }))}
                                        placeholder="Add any remarks about the trip..."
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-sm font-medium text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-900 focus:border-teal-500 dark:focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 placeholder-slate-400 dark:placeholder-slate-500 transition-all resize-none h-24"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setCompleteModal({ isOpen: false, type: null, entityId: null })}
                                    className="px-4 py-3.5 rounded-xl text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 font-bold text-xs tracking-widest uppercase transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmComplete}
                                    className="px-4 py-3.5 rounded-xl text-white bg-slate-900 dark:bg-teal-600 hover:bg-black dark:hover:bg-teal-700 shadow-lg shadow-black/20 dark:shadow-teal-900/50 font-bold text-xs tracking-widest uppercase transition-all transform active:scale-95"
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <BottomNav items={driverMenuItems} />
        </div>
    );
};

export default MyRoutes;
